"use client"

import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { 
  FileText, Shield, Plus, Search, Filter, RefreshCw, MoreVertical,
  Settings, Edit, Trash2, Users, CheckCircle, AlertCircle, X,
  Eye, EyeOff, ShieldCheck, UserPlus, Clock, Calendar
} from 'lucide-react'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../ui/dropdown-menu.jsx'
import { VisuallyHidden } from '../ui/visually-hidden.jsx'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

// Performance optimized Team Policies component
export function TeamPolicies() {
  const [policies, setPolicies] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingPolicy, setEditingPolicy] = useState(null)
  const [selectedPolicy, setSelectedPolicy] = useState(null)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [error, setError] = useState('')
  const [stats, setStats] = useState({ total: 0, active: 0, assignments: 0 })
  const [roles, setRoles] = useState([])
  const [permissions, setPermissions] = useState([])

  // Load policies
  const loadPolicies = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      
      const params = new URLSearchParams()
      if (search) params.append('search', search)
      if (roleFilter) params.append('role_filter', roleFilter)
      params.append('active_only', 'true')

      const { data } = await axios.get(`${API}/admin/team/policies?${params}`, { 
        withCredentials: true 
      })
      
      setPolicies(data.policies || [])
      setStats({
        total: data.total || 0,
        active: data.policies?.filter(p => p.is_active).length || 0,
        assignments: data.policies?.reduce((sum, p) => sum + (p.assignment_count || 0), 0) || 0
      })
      
    } catch (err) {
      setError('Failed to load policy bundles')
      console.error('Policy loading error:', err)
    } finally {
      setLoading(false)
    }
  }, [search, roleFilter])

  // Load supporting data
  useEffect(() => {
    const loadSupportingData = async () => {
      try {
        // Load roles for filtering and assignment
        const rolesResponse = await axios.get(`${API}/rbac/roles`, { withCredentials: true })
        const teamRoles = rolesResponse.data.filter(role => role.key !== 'subscriber')
        setRoles(teamRoles)
        
        // Load permissions for policy creation
        const permissionsResponse = await axios.get(`${API}/rbac/permissions`, { withCredentials: true })
        setPermissions(permissionsResponse.data)
        
      } catch (err) {
        console.error('Failed to load supporting data:', err)
      }
    }
    
    loadSupportingData()
  }, [])

  useEffect(() => {
    loadPolicies()
  }, [loadPolicies])

  return (
    <div className="team-policies mobile-full-width">
      {/* Header with stats and controls */}
      <div className="flex-col-mobile items-start justify-between mb-6 mobile-full-width">
        <div className="w-full mobile-full-width">
          <h2 className="text-2xl font-semibold text-primary mb-2">Policy Bundles</h2>
          <div className="flex-col-mobile gap-4 text-sm text-muted mobile-full-width">
            <div className="flex gap-4 flex-wrap mobile-full-width">
              <span><FileText size={16} className="inline mr-1" />{stats.total} Total</span>
              <span><CheckCircle size={16} className="inline mr-1 text-green-600" />{stats.active} Active</span>
              <span><Users size={16} className="inline mr-1 text-blue-600" />{stats.assignments} Assignments</span>
            </div>
          </div>
        </div>
        
        <div className="flex gap-2 w-full sm:w-auto mobile-full-width">
          <button 
            onClick={loadPolicies} 
            className="btn secondary"
            disabled={loading}
            aria-label="Refresh policy bundles"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} aria-hidden="true" />
            <VisuallyHidden>
              {loading ? 'Refreshing policy data' : 'Reload policy bundles from server'}
            </VisuallyHidden>
          </button>
          
          <button 
            onClick={() => setShowCreateModal(true)}
            className="btn primary"
            aria-label="Create new policy bundle"
          >
            <Plus size={16} aria-hidden="true" />
            <span className="hidden sm:inline ml-2">Create Policy</span>
            <VisuallyHidden>
              Open form to create a new policy bundle
            </VisuallyHidden>
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex-col-mobile gap-3 mb-6 mobile-full-width">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted" size={16} />
          <input
            type="text"
            placeholder="Search policy bundles..."
            className="input pl-10 w-full"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search policy bundles by name or description"
          />
        </div>
        
        <div className="w-full sm:w-48">
          <select
            className="input w-full"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            aria-label="Filter by applicable role"
          >
            <option value="">All Roles</option>
            {roles.map(role => (
              <option key={role.id} value={role.key}>
                {role.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error mb-4" role="alert">
          {error}
        </div>
      )}

      {/* Policy Bundles */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-pulse">Loading policy bundles...</div>
        </div>
      ) : policies.length === 0 ? (
        <div className="text-center py-8">
          <FileText size={48} className="mx-auto text-muted mb-4" />
          <p className="text-muted">No policy bundles found</p>
          <button 
            onClick={() => setShowCreateModal(true)}
            className="btn primary mt-4"
          >
            <Plus size={16} />
            Create First Policy
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {policies.map((policy) => (
            <PolicyCard
              key={policy.id}
              policy={policy}
              onSelect={() => {
                setSelectedPolicy(policy)
                setShowDetailsModal(true)
              }}
              onEdit={() => {
                setEditingPolicy(policy)
                setShowEditModal(true)
              }}
              onRefresh={loadPolicies}
            />
          ))}
        </div>
      )}

      {/* Create Policy Modal */}
      {showCreateModal && (
        <CreatePolicyModal
          roles={roles}
          permissions={permissions}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false)
            loadPolicies()
          }}
        />
      )}

      {/* Edit Policy Modal */}
      {showEditModal && editingPolicy && (
        <EditPolicyModal
          policy={editingPolicy}
          roles={roles}
          permissions={permissions}
          onClose={() => {
            setShowEditModal(false)
            setEditingPolicy(null)
          }}
          onSuccess={() => {
            setShowEditModal(false)
            setEditingPolicy(null)
            loadPolicies()
          }}
        />
      )}

      {/* Policy Details Modal */}
      {showDetailsModal && selectedPolicy && (
        <PolicyDetailsModal
          policy={selectedPolicy}
          roles={roles}
          onClose={() => setShowDetailsModal(false)}
          onRefresh={loadPolicies}
        />
      )}
    </div>
  )
}

// Individual Policy Card Component
function PolicyCard({ policy, onSelect, onEdit, onRefresh }) {
  const [actionLoading, setActionLoading] = useState(false)

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set'
    const date = new Date(dateString)
    return date.toLocaleDateString()
  }

  const performAction = async (action) => {
    try {
      setActionLoading(true)
      
      if (action === 'deactivate') {
        const justification = window.prompt('Please provide justification for deactivating this policy:')
        if (!justification) return
        
        await axios.patch(
          `${API}/admin/team/policies/${policy.id}`,
          { is_active: false, justification },
          { withCredentials: true }
        )
        
        onRefresh()
      }
      
    } catch (err) {
      alert(`Action failed: ${err.response?.data?.detail || err.message}`)
    } finally {
      setActionLoading(false)
    }
  }

  return (
    <div className="card p-4 hover:shadow-md transition-shadow mobile-full-width">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-primary mb-1 truncate">{policy.name}</h3>
          <p className="text-sm text-muted line-clamp-2">{policy.description}</p>
        </div>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="icon-btn" disabled={actionLoading} aria-label={`Actions for policy ${policy.name}`}>
              <MoreVertical size={16} aria-hidden="true" />
              <VisuallyHidden>
                Open actions menu for policy {policy.name}
              </VisuallyHidden>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onSelect}>
              <Eye size={14} className="mr-2" aria-hidden="true" />
              View Details
              <VisuallyHidden>View detailed information for {policy.name}</VisuallyHidden>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onEdit}>
              <Edit size={14} className="mr-2" aria-hidden="true" />
              Edit Policy
              <VisuallyHidden>Edit {policy.name} policy configuration</VisuallyHidden>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={() => performAction('deactivate')}
              className="text-red-600"
            >
              <Trash2 size={14} className="mr-2" aria-hidden="true" />
              Deactivate
              <VisuallyHidden>Deactivate {policy.name} policy</VisuallyHidden>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="space-y-3">
        {/* Policy Stats */}
        <div className="flex items-center gap-4 flex-wrap text-sm">
          <span className="flex items-center gap-1">
            <ShieldCheck size={14} className="text-blue-600" />
            {policy.permissions?.length || 0} permissions
          </span>
          <span className="flex items-center gap-1">
            <Users size={14} className="text-green-600" />
            {policy.assignment_count || 0} assignments
          </span>
          {policy.approval_required && (
            <span className="flex items-center gap-1 text-orange-600">
              <AlertCircle size={14} />
              Approval required
            </span>
          )}
        </div>

        {/* Assigned Roles */}
        {policy.assigned_roles && policy.assigned_roles.length > 0 && (
          <div>
            <h5 className="text-xs font-medium text-primary mb-1">Assigned to:</h5>
            <div className="flex flex-wrap gap-1">
              {policy.assigned_roles.slice(0, 3).map((assignment) => (
                <span key={assignment.role_id} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                  {assignment.role_name}
                </span>
              ))}
              {policy.assigned_roles.length > 3 && (
                <span className="text-xs bg-muted-100 text-muted px-2 py-1 rounded">
                  +{policy.assigned_roles.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Policy Metadata */}
        <div className="text-xs text-muted border-t border-muted-200 pt-2">
          <div className="flex justify-between">
            <span>Version {policy.version || 1}</span>
            <span>Created {formatDate(policy.created_at)}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

// Create Policy Modal Component
function CreatePolicyModal({ roles, permissions, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: [],
    applicable_roles: [],
    constraints: {},
    approval_required: false,
    justification: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [selectedModule, setSelectedModule] = useState('')

  // Group permissions by module
  const permissionsByModule = {}
  permissions.forEach(permission => {
    const module = permission.module
    if (!permissionsByModule[module]) permissionsByModule[module] = []
    permissionsByModule[module].push(permission)
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      await axios.post(`${API}/admin/team/policies`, formData, { withCredentials: true })
      onSuccess()
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create policy bundle')
    } finally {
      setLoading(false)
    }
  }

  const togglePermission = (permissionKey) => {
    const newPermissions = formData.permissions.includes(permissionKey)
      ? formData.permissions.filter(p => p !== permissionKey)
      : [...formData.permissions, permissionKey]
    
    setFormData({...formData, permissions: newPermissions})
  }

  const toggleRole = (roleKey) => {
    const newRoles = formData.applicable_roles.includes(roleKey)
      ? formData.applicable_roles.filter(r => r !== roleKey)
      : [...formData.applicable_roles, roleKey]
    
    setFormData({...formData, applicable_roles: newRoles})
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-canvas rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-muted-200">
          <h3 className="text-xl font-semibold text-primary">Create Policy Bundle</h3>
          <button 
            onClick={onClose}
            className="icon-btn"
            aria-label="Close create policy modal"
          >
            <X size={18} aria-hidden="true" />
            <VisuallyHidden>Close policy creation modal</VisuallyHidden>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-primary mb-2">
                Policy Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="input w-full"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                placeholder="e.g., Support Operations Bundle"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-primary mb-2">
                Approval Required
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.approval_required}
                  onChange={(e) => setFormData({...formData, approval_required: e.target.checked})}
                />
                <span className="text-sm">Require approval for assignment</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              className="input w-full"
              rows="3"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              required
              placeholder="Describe the purpose and scope of this policy bundle..."
            />
          </div>

          {/* Permissions Selection */}
          <div>
            <label className="block text-sm font-medium text-primary mb-3">
              Permissions ({formData.permissions.length} selected)
            </label>
            
            <div className="mb-3">
              <select
                className="input w-full sm:w-64"
                value={selectedModule}
                onChange={(e) => setSelectedModule(e.target.value)}
              >
                <option value="">All Modules</option>
                {Object.keys(permissionsByModule).map(module => (
                  <option key={module} value={module}>
                    {module.charAt(0).toUpperCase() + module.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div className="max-h-64 overflow-y-auto border border-muted-200 rounded-lg p-3">
              {Object.entries(permissionsByModule)
                .filter(([module]) => !selectedModule || module === selectedModule)
                .map(([module, modulePermissions]) => (
                  <div key={module} className="mb-4">
                    <h5 className="text-sm font-medium text-primary mb-2 uppercase tracking-wide">
                      {module} ({modulePermissions.length})
                    </h5>
                    <div className="space-y-2">
                      {modulePermissions.map((permission) => (
                        <label key={permission.id} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={formData.permissions.includes(permission.key)}
                            onChange={() => togglePermission(permission.key)}
                          />
                          <span className="flex-1">
                            {permission.name}
                            <span className="text-muted ml-2 font-mono text-xs">
                              ({permission.key})
                            </span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Applicable Roles */}
          <div>
            <label className="block text-sm font-medium text-primary mb-3">
              Applicable Roles ({formData.applicable_roles.length} selected)
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {roles.map((role) => (
                <label key={role.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={formData.applicable_roles.includes(role.key)}
                    onChange={() => toggleRole(role.key)}
                  />
                  <span className="truncate">{role.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Justification */}
          <div>
            <label className="block text-sm font-medium text-primary mb-2">
              Justification <span className="text-red-500">*</span>
            </label>
            <textarea
              className="input w-full"
              rows="3"
              value={formData.justification}
              onChange={(e) => setFormData({...formData, justification: e.target.value})}
              required
              placeholder="Explain the business need for this policy bundle..."
            />
          </div>

          {error && (
            <div className="error" role="alert">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-4 border-t border-muted-200">
            <button
              type="submit"
              className="btn primary flex-1"
              disabled={loading || !formData.name || !formData.description || formData.permissions.length === 0 || !formData.justification}
            >
              {loading ? 'Creating...' : 'Create Policy Bundle'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn secondary flex-1"
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Policy Details Modal Component
function PolicyDetailsModal({ policy, roles, onClose, onRefresh }) {
  const [assignments, setAssignments] = useState([])
  const [loading, setLoading] = useState(false)
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const loadPolicyDetails = async () => {
      try {
        setLoading(true)
        const { data } = await axios.get(`${API}/admin/team/policies/${policy.id}`, { 
          withCredentials: true 
        })
        setAssignments(data.assignments || [])
      } catch (err) {
        setError('Failed to load policy details')
        console.error('Policy details error:', err)
      } finally {
        setLoading(false)
      }
    }
    
    loadPolicyDetails()
  }, [policy.id])

  const unassignPolicy = async (assignmentId) => {
    const justification = window.prompt('Please provide justification for removing this policy assignment:')
    if (!justification) return

    try {
      await axios.delete(
        `${API}/admin/team/policies/${policy.id}/assign/${assignmentId}`,
        {
          data: { justification },
          withCredentials: true
        }
      )
      
      // Refresh assignments
      setAssignments(assignments.filter(a => a.assignment_id !== assignmentId))
      onRefresh()
      
    } catch (err) {
      alert(`Unassignment failed: ${err.response?.data?.detail || err.message}`)
    }
  }

  const groupPermissions = (permissionKeys) => {
    const groups = {}
    permissionKeys.forEach(key => {
      const module = key.split(':')[0]
      if (!groups[module]) groups[module] = []
      groups[module].push(key)
    })
    return groups
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-canvas rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-muted-200">
          <h3 className="text-xl font-semibold text-primary">Policy Details</h3>
          <button 
            onClick={onClose}
            className="icon-btn"
            aria-label="Close policy details modal"
          >
            <X size={18} aria-hidden="true" />
            <VisuallyHidden>Close policy details modal</VisuallyHidden>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h4 className="text-lg font-medium text-primary mb-3">{policy.name}</h4>
            <p className="text-muted mb-4">{policy.description}</p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-muted">Version:</span>
                <span className="ml-2 font-medium">{policy.version || 1}</span>
              </div>
              <div>
                <span className="text-muted">Permissions:</span>
                <span className="ml-2 font-medium">{policy.permissions?.length || 0}</span>
              </div>
              <div>
                <span className="text-muted">Assignments:</span>
                <span className="ml-2 font-medium">{assignments.length}</span>
              </div>
              <div>
                <span className="text-muted">Approval:</span>
                <span className={`ml-2 font-medium ${policy.approval_required ? 'text-orange-600' : 'text-green-600'}`}>
                  {policy.approval_required ? 'Required' : 'Not required'}
                </span>
              </div>
            </div>
          </div>

          {/* Permissions */}
          <div className="border-t border-muted-200 pt-4">
            <h5 className="text-sm font-medium text-primary mb-3">
              Permissions ({policy.permissions?.length || 0})
            </h5>
            {policy.permissions && policy.permissions.length > 0 ? (
              <div className="space-y-3">
                {Object.entries(groupPermissions(policy.permissions)).map(([module, perms]) => (
                  <div key={module}>
                    <h6 className="text-xs font-medium text-primary uppercase tracking-wide mb-2">
                      {module}
                    </h6>
                    <div className="flex flex-wrap gap-2">
                      {perms.map((perm, index) => (
                        <span key={index} className="text-xs bg-primary-50 text-primary px-2 py-1 rounded border border-primary-200">
                          {perm.split(':')[1]}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted italic">No permissions defined</p>
            )}
          </div>

          {/* Role Assignments */}
          <div className="border-t border-muted-200 pt-4">
            <div className="flex items-center justify-between mb-3">
              <h5 className="text-sm font-medium text-primary">
                Role Assignments ({assignments.length})
              </h5>
              <button
                onClick={() => setShowAssignModal(true)}
                className="btn secondary text-xs"
              >
                <UserPlus size={14} />
                Assign to Role
              </button>
            </div>

            {loading ? (
              <div className="text-center py-4">
                <div className="animate-pulse text-muted">Loading assignments...</div>
              </div>
            ) : assignments.length === 0 ? (
              <p className="text-sm text-muted italic">No role assignments</p>
            ) : (
              <div className="space-y-2">
                {assignments.map((assignment) => (
                  <div key={assignment.assignment_id} className="flex items-center justify-between p-3 bg-muted-50 rounded">
                    <div>
                      <span className="font-medium text-primary">{assignment.role_name}</span>
                      <div className="text-xs text-muted">
                        Assigned {new Date(assignment.assigned_at).toLocaleDateString()}
                      </div>
                    </div>
                    <button
                      onClick={() => unassignPolicy(assignment.assignment_id)}
                      className="btn secondary text-xs text-red-600"
                    >
                      <Trash2 size={12} />
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Close Actions */}
          <div className="border-t border-muted-200 pt-4">
            <div className="flex justify-end">
              <button 
                onClick={onClose}
                className="btn secondary"
              >
                Close Details
              </button>
            </div>
          </div>
        </div>

        {/* Assign Policy Modal */}
        {showAssignModal && (
          <AssignPolicyModal
            policy={policy}
            roles={roles}
            existingAssignments={assignments}
            onClose={() => setShowAssignModal(false)}
            onSuccess={() => {
              setShowAssignModal(false)
              onRefresh()
            }}
          />
        )}
      </div>
    </div>
  )
}

// Assign Policy to Role Modal Component
function AssignPolicyModal({ policy, roles, existingAssignments, onClose, onSuccess }) {
  const [selectedRoleId, setSelectedRoleId] = useState('')
  const [justification, setJustification] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Filter out already assigned roles
  const assignedRoleIds = new Set(existingAssignments.map(a => a.role_id))
  const availableRoles = roles.filter(role => !assignedRoleIds.has(role.id))

  const handleAssign = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      await axios.post(
        `${API}/admin/team/policies/${policy.id}/assign`,
        {
          role_id: selectedRoleId,
          justification: justification.trim()
        },
        { withCredentials: true }
      )
      
      onSuccess()
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to assign policy')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-canvas rounded-lg p-6 w-full max-w-md">
        <h4 className="text-lg font-semibold text-primary mb-4">Assign Policy to Role</h4>
        
        <form onSubmit={handleAssign} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-primary mb-2">
              Select Role
            </label>
            <select
              className="input w-full"
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
              required
            >
              <option value="">Choose a role...</option>
              {availableRoles.map(role => (
                <option key={role.id} value={role.id}>
                  {role.name} (Level {role.hierarchy_level})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-2">
              Justification <span className="text-red-500">*</span>
            </label>
            <textarea
              className="input w-full"
              rows="3"
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              placeholder="Explain why this policy should be assigned to the selected role..."
              required
            />
          </div>

          {error && (
            <div className="error" role="alert">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="submit"
              className="btn primary flex-1"
              disabled={loading || !selectedRoleId || !justification.trim()}
            >
              {loading ? 'Assigning...' : 'Assign Policy'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn secondary flex-1"
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Edit Policy Modal Component
function EditPolicyModal({ policy, roles, permissions, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: policy.name || '',
    description: policy.description || '',
    permissions: policy.permissions || [],
    applicable_roles: policy.applicable_roles || [],
    constraints: policy.constraints || {},
    approval_required: policy.approval_required || false,
    justification: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [selectedModule, setSelectedModule] = useState('')

  // Group permissions by module
  const permissionsByModule = {}
  permissions.forEach(permission => {
    const module = permission.module
    if (!permissionsByModule[module]) permissionsByModule[module] = []
    permissionsByModule[module].push(permission)
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      // Only send fields that might have changed
      const updateData = {
        name: formData.name,
        description: formData.description,
        permissions: formData.permissions,
        applicable_roles: formData.applicable_roles,
        constraints: formData.constraints,
        approval_required: formData.approval_required,
        justification: formData.justification
      }

      await axios.patch(
        `${API}/admin/team/policies/${policy.id}`, 
        updateData, 
        { withCredentials: true }
      )
      
      onSuccess()
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update policy bundle')
    } finally {
      setLoading(false)
    }
  }

  const togglePermission = (permissionKey) => {
    const newPermissions = formData.permissions.includes(permissionKey)
      ? formData.permissions.filter(p => p !== permissionKey)
      : [...formData.permissions, permissionKey]
    
    setFormData({...formData, permissions: newPermissions})
  }

  const toggleRole = (roleKey) => {
    const newRoles = formData.applicable_roles.includes(roleKey)
      ? formData.applicable_roles.filter(r => r !== roleKey)
      : [...formData.applicable_roles, roleKey]
    
    setFormData({...formData, applicable_roles: newRoles})
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-canvas rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-muted-200">
          <h3 className="text-xl font-semibold text-primary">Edit Policy Bundle</h3>
          <button 
            onClick={onClose}
            className="icon-btn"
            aria-label="Close edit policy modal"
          >
            <X size={18} aria-hidden="true" />
            <VisuallyHidden>Close policy editing modal</VisuallyHidden>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Current Policy Information */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-800 mb-2">Editing Policy: {policy.name}</h4>
            <div className="text-sm text-blue-600 grid grid-cols-2 md:grid-cols-4 gap-4">
              <span>Version: {policy.version || 1}</span>
              <span>Permissions: {policy.permissions?.length || 0}</span>
              <span>Assignments: {policy.assignment_count || 0}</span>
              <span>Created: {new Date(policy.created_at).toLocaleDateString()}</span>
            </div>
          </div>

          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-primary mb-2">
                Policy Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="input w-full"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                placeholder="e.g., Support Operations Bundle"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-primary mb-2">
                Approval Required
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.approval_required}
                  onChange={(e) => setFormData({...formData, approval_required: e.target.checked})}
                />
                <span className="text-sm">Require approval for assignment</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              className="input w-full"
              rows="3"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              required
              placeholder="Describe the purpose and scope of this policy bundle..."
            />
          </div>

          {/* Permissions Selection */}
          <div>
            <label className="block text-sm font-medium text-primary mb-3">
              Permissions ({formData.permissions.length} selected)
            </label>
            
            <div className="mb-3">
              <select
                className="input w-full sm:w-64"
                value={selectedModule}
                onChange={(e) => setSelectedModule(e.target.value)}
              >
                <option value="">All Modules</option>
                {Object.keys(permissionsByModule).map(module => (
                  <option key={module} value={module}>
                    {module.charAt(0).toUpperCase() + module.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div className="max-h-64 overflow-y-auto border border-muted-200 rounded-lg p-3">
              {Object.entries(permissionsByModule)
                .filter(([module]) => !selectedModule || module === selectedModule)
                .map(([module, modulePermissions]) => (
                  <div key={module} className="mb-4">
                    <h5 className="text-sm font-medium text-primary mb-2 uppercase tracking-wide">
                      {module} ({modulePermissions.length})
                    </h5>
                    <div className="space-y-2">
                      {modulePermissions.map((permission) => (
                        <label key={permission.id} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={formData.permissions.includes(permission.key)}
                            onChange={() => togglePermission(permission.key)}
                          />
                          <span className="flex-1">
                            {permission.name}
                            <span className="text-muted ml-2 font-mono text-xs">
                              ({permission.key})
                            </span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Applicable Roles */}
          <div>
            <label className="block text-sm font-medium text-primary mb-3">
              Applicable Roles ({formData.applicable_roles.length} selected)
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {roles.map((role) => (
                <label key={role.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={formData.applicable_roles.includes(role.key)}
                    onChange={() => toggleRole(role.key)}
                  />
                  <span className="truncate">{role.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Change Justification */}
          <div>
            <label className="block text-sm font-medium text-primary mb-2">
              Change Justification <span className="text-red-500">*</span>
            </label>
            <textarea
              className="input w-full"
              rows="3"
              value={formData.justification}
              onChange={(e) => setFormData({...formData, justification: e.target.value})}
              required
              placeholder="Explain the reason for updating this policy bundle..."
            />
            <p className="text-xs text-muted mt-1">
              This will be recorded in the audit log for compliance purposes.
            </p>
          </div>

          {/* Change Summary */}
          <div className="p-4 bg-muted-50 border border-muted-200 rounded-lg">
            <h5 className="text-sm font-medium text-primary mb-2">Changes Summary</h5>
            <div className="text-sm text-muted space-y-1">
              {formData.name !== policy.name && (
                <p>• Name: "{policy.name}" → "{formData.name}"</p>
              )}
              {formData.description !== policy.description && (
                <p>• Description updated</p>
              )}
              {formData.permissions.length !== (policy.permissions?.length || 0) && (
                <p>• Permissions: {policy.permissions?.length || 0} → {formData.permissions.length}</p>
              )}
              {formData.applicable_roles.length !== (policy.applicable_roles?.length || 0) && (
                <p>• Applicable roles: {policy.applicable_roles?.length || 0} → {formData.applicable_roles.length}</p>
              )}
              {formData.approval_required !== policy.approval_required && (
                <p>• Approval requirement: {policy.approval_required ? 'Required' : 'Not required'} → {formData.approval_required ? 'Required' : 'Not required'}</p>
              )}
              {formData.name === policy.name && 
               formData.description === policy.description && 
               formData.permissions.length === (policy.permissions?.length || 0) &&
               formData.applicable_roles.length === (policy.applicable_roles?.length || 0) &&
               formData.approval_required === policy.approval_required && (
                <p className="italic">No changes detected</p>
              )}
            </div>
          </div>

          {error && (
            <div className="error" role="alert">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-4 border-t border-muted-200">
            <button
              type="submit"
              className="btn primary flex-1"
              disabled={loading || !formData.name || !formData.description || formData.permissions.length === 0 || !formData.justification}
            >
              {loading ? 'Updating...' : 'Update Policy Bundle'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn secondary flex-1"
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default TeamPolicies